import { Directive, ElementRef, Input, OnChanges } from '@angular/core';
import { GridColumn } from './data-grid.model';

/**
 * Calls a DevExtreme-shaped `onCellPrepared` handler for each rendered cell.
 *
 * WHY
 * ---
 * 32 onCellPrepared handlers remain across ~30 files, and 28 of them do nothing
 * but cosmetics: tint a clickable column blue, underline it, set cursor:pointer,
 * occasionally a title tooltip. Rewriting each into a column `cssClass` means
 * reading 28 handlers, mapping their dataField conditions, and getting all of it
 * right - with the failure mode being a column that silently stops looking
 * clickable.
 *
 * Handing them the shape they were written against instead means they port with
 * NO edit at all:
 *
 *     e.rowType === 'data' && e.column.dataField === 'x'
 *     e.cellElement.style.color = '#2563eb'
 *     e.cellElement.title = 'Click to open'
 *
 * This is the same move that made cellClick, saved, selectionChanged and the
 * cell template context work: match DevExtreme's contract and the host code
 * needs no translation.
 *
 * `row` and `column` are separate inputs rather than one object literal on
 * purpose - a literal rebuilt in the template would make ngOnChanges fire for
 * every cell on every change-detection pass.
 */
@Directive({ selector: '[appGridCellHook]', standalone: false })
export class GridCellHookDirective implements OnChanges {
  /** The host's onCellPrepared function, or undefined to do nothing. */
  @Input('appGridCellHook') handler?: (e: any) => void;
  @Input() hookRow: any;
  @Input() hookColumn?: GridColumn;
  @Input() hookValue: any;

  constructor(private el: ElementRef<HTMLElement>) { }

  ngOnChanges(): void {
    if (typeof this.handler !== 'function') { return; }
    this.handler({
      rowType: 'data',
      data: this.hookRow,
      // DevExtreme exposes the row wrapper as `row`, with the record on
      // `row.data`. Several handlers guard on `e.row.data` before touching
      // anything, so both spellings have to be present.
      row: { data: this.hookRow, rowType: 'data' },
      column: this.hookColumn,
      value: this.hookValue,
      cellElement: this.el.nativeElement,
    });
  }
}
